import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Modal,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Stack,
  Grid,
} from "@mui/material";
import {
  CheckCircle,
  Visibility,
  Person,
  Comment,
  Schedule,
  CalendarToday,
} from "@mui/icons-material";
import { getAllTickets } from "../services/api";
import { convertToRawDate } from "../shared/base";

const ResolvedTickets = () => {
  const [resolvedTickets, setResolvedTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const handleOpenDetails = (ticket) => {
    setSelectedTicket(ticket);
  };

  const handleCloseDetails = () => {
    setSelectedTicket(null);
  };

  const _getAllTickets = async () => {
    const getAllTicketsApi = await getAllTickets({
      FilterField: "TicketStatus",
      Value: "Approved",
    });
    if (getAllTicketsApi && getAllTicketsApi.success) {
      setResolvedTickets(getAllTicketsApi.data);
    }
  };

  useEffect(() => {
    _getAllTickets();
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom color="#d32f2f">
        Resolved Tickets
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: "#ffebee" }}>
            <TableRow>
              <TableCell>Ticket</TableCell>
              <TableCell>Assignee</TableCell>
              <TableCell>Timeline</TableCell>
              <TableCell>Resolution Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {resolvedTickets.map((ticket) => (
              <TableRow key={ticket.ID}>
                <TableCell>
                  <Typography fontWeight="bold">{ticket.Title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {ticket.Description.substring(0, 50)}...
                  </Typography>
                  <Chip
                    label={ticket.Priority}
                    size="small"
                    color={
                      ticket.Priority === "High"
                        ? "error"
                        : ticket.Priority === "Medium"
                        ? "warning"
                        : "success"
                    }
                    sx={{ mt: 1 }}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        mr: 1,
                        bgcolor:
                          ticket?._Assignee?._Role.Name === "Sales"
                            ? "#d32f2f"
                            : "#7b1fa2",
                      }}
                    >
                      {ticket?._Assignee?.FirstName.charAt(0)}
                    </Avatar>
                    {`${ticket?._Assignee?.FirstName} ${ticket?._Assignee?.LastName}`}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <CalendarToday
                      sx={{ verticalAlign: "middle", fontSize: 16, mr: 0.5 }}
                    />
                    {convertToRawDate(ticket.ActualStartDate)} to{" "}
                    {convertToRawDate(ticket.ActualEndDate)}
                  </Typography>
                  <Typography variant="caption" display="block">
                    {ticket.LeadTime} (Actual)
                  </Typography>
                </TableCell>
                <TableCell>{convertToRawDate(ticket.ActualEndDate)}</TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    startIcon={<Visibility />}
                    onClick={() => handleOpenDetails(ticket)}
                    color="error"
                  >
                    Details
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Ticket Details Modal */}
      <Modal
        open={Boolean(selectedTicket)}
        onClose={handleCloseDetails}
        aria-labelledby="ticket-details-modal"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "80%",
            maxWidth: 800,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          {selectedTicket && (
            <>
              <Typography variant="h5" gutterBottom color="#d32f2f">
                {selectedTicket.Title}
              </Typography>

              <Typography variant="body1" paragraph>
                {selectedTicket.Description}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={3}>
                {/* Left Column - Ticket Info */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom color="#d32f2f">
                    Ticket Information
                  </Typography>

                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle2">Requester</Typography>
                      <Typography>{`${selectedTicket?._Reporter?.FirstName} ${selectedTicket?._Reporter?.LastName}`}</Typography>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2">Assignee</Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mt: 1 }}
                      >
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            mr: 1,
                            bgcolor:
                              selectedTicket?._Assignee?._Role.Name === "Sales"
                                ? "#d32f2f"
                                : "#7b1fa2",
                          }}
                        >
                          {selectedTicket?._Assignee?.FirstName.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography>
                            {`${selectedTicket?._Assignee?.FirstName} ${selectedTicket?._Assignee?.LastName}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {selectedTicket?.Assignee?._Role?.Name}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2">Priority</Typography>
                      <Chip
                        label={selectedTicket.Priority}
                        color={
                          selectedTicket.Priority === "High"
                            ? "error"
                            : selectedTicket.Priority === "Medium"
                            ? "warning"
                            : "success"
                        }
                      />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2">Category</Typography>
                      <Chip label={selectedTicket.Category} color={"success"} />
                    </Box>
                  </Stack>
                </Grid>

                {/* Right Column - Timeline */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom color="#d32f2f">
                    Timeline
                  </Typography>

                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle2">Requested</Typography>
                      <Typography>
                        {convertToRawDate(selectedTicket.ExpectedStartDate)} to{" "}
                        {convertToRawDate(selectedTicket.ExpectedEndDate)}
                      </Typography>
                      <Typography variant="caption" display="block">
                        {selectedTicket.LeadTime} (Estimated)
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2">Actual</Typography>
                      <Typography>
                        {convertToRawDate(selectedTicket.ActualStartDate)} to{" "}
                        {convertToRawDate(selectedTicket.ActualEndDate)}
                      </Typography>
                      <Typography variant="caption" display="block">
                        {Math.round(
                          (new Date(selectedTicket.ActualEndDate) -
                            new Date(selectedTicket.ActualStartDate)) /
                            (1000 * 60 * 60 * 24)
                        )}{" "}
                        days (Actual)
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2">
                        Resolution Date
                      </Typography>
                      <Typography>
                        {convertToRawDate(selectedTicket.ActualEndDate)}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Approval History */}
              <Typography variant="h6" gutterBottom color="#d32f2f">
                Approval History
              </Typography>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#ffebee" }}>
                      <TableCell>Level</TableCell>
                      <TableCell>Approver</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Comment</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedTicket._Approvals &&
                      selectedTicket._Approvals.map((approval, index) => (
                        <TableRow key={index}>
                          <TableCell>{approval?.ApprovalType}</TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Person sx={{ mr: 1, color: "action.active" }} />
                              {`${approval?._Approver?.FirstName} ${approval?._Approver?.LastName}`}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {convertToRawDate(approval?.createdAt)}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Comment sx={{ mr: 1, color: "action.active" }} />
                              {approval.Comment}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={approval.Status ? "Approved" : "Reject"}
                              color="success"
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleCloseDetails}
                  color="error"
                >
                  Close
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default ResolvedTickets;
